import { CopyOutlined, FolderOpenOutlined, SelectOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Dropdown, message, Tooltip } from 'antd';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

interface MenuItemWithContextProps {
    route: string;
    children: React.ReactNode;
    className?: string;
    enableContextMenu?: boolean;
    customContextItems?: MenuProps['items'];
    onOpenNewTab?: () => void;
    onCopyLink?: () => void;
    tooltip?: string;
    tooltipPlacement?: 'right' | 'left' | 'top' | 'bottom';
}

export const MenuItemWithContext = ({
    route,
    children,
    className,
    enableContextMenu = true,
    customContextItems,
    onOpenNewTab,
    onCopyLink,
    tooltip,
    tooltipPlacement = 'right',
}: MenuItemWithContextProps) => {
    const navigate = useNavigate();
    const [contextMenuOpen, setContextMenuOpen] = useState(false);

    const defaultContextMenuItems: MenuProps['items'] = [
        {
            key: 'open',
            label: 'Open',
            title: '',
            icon: <FolderOpenOutlined style={{ fontSize: 14 }} />,
            onClick: () => navigate(route),
        },
        {
            key: 'open-new-tab',
            label: 'Open in New Tab',
            title: '',
            icon: <SelectOutlined style={{ fontSize: 14 }} />,
            onClick: () => {
                if (onOpenNewTab) {
                    onOpenNewTab();
                } else {
                    window.open(route, '_blank');
                }
            },
        },
        {
            key: 'copy-link',
            label: 'Copy Link Address',
            title: '',
            icon: <CopyOutlined style={{ fontSize: 14 }} />,
            onClick: async () => {
                try {
                    await navigator.clipboard.writeText(window.location.origin + route);
                    message.success('Link copied to clipboard');
                    onCopyLink?.();
                } catch {
                    message.error('Failed to copy link');
                }
            },
        },
    ];

    const contextMenuItems = customContextItems || defaultContextMenuItems;

    if (!enableContextMenu) {
        return tooltip ? (
            <Tooltip
                title={tooltip}
                placement={tooltipPlacement}
                mouseEnterDelay={0.3}
                mouseLeaveDelay={0}
                overlayStyle={{ pointerEvents: 'none' }}
            >
                <Link
                    to={route}
                    className={className || 'block h-full w-full'}
                >
                    {children}
                </Link>
            </Tooltip>
        ) : (
            <Link
                to={route}
                className={className || 'block h-full w-full'}
            >
                {children}
            </Link>
        );
    }

    return (
        <Dropdown
            menu={{ items: contextMenuItems }}
            trigger={['contextMenu']}
            open={contextMenuOpen}
            onOpenChange={setContextMenuOpen}
            overlayClassName="context-menu-overlay"
            destroyOnHidden
        >
            <div
                onContextMenu={e => {
                    e.preventDefault();
                    setContextMenuOpen(true);
                }}
            >
                {tooltip ? (
                    <Tooltip
                        title={tooltip}
                        placement={tooltipPlacement}
                        mouseEnterDelay={0.3}
                        mouseLeaveDelay={0}
                        styles={{ root: { pointerEvents: 'none' } }}
                    >
                        <Link
                            to={route}
                            className={className || 'block h-full w-full'}
                        >
                            {children}
                        </Link>
                    </Tooltip>
                ) : (
                    <Link
                        to={route}
                        className={className || 'block h-full w-full'}
                    >
                        {children}
                    </Link>
                )}
            </div>
        </Dropdown>
    );
};
